import {
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  addDoc,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  runTransaction,
  collection,
} from 'firebase/firestore';
import { db, auth, functions, httpsCallable } from '../firebase';
import { getPatientRpmCareManagerIds } from '../utils/patientAccessScope';
import { normalizeYmdDate } from '../utils/dateFormat';
import { recordDailyWin } from '../utils/dailyWins';
import { logPatientAuditCreate, logPatientAuditUpdate, computePatientFieldChanges } from './patientAuditService';
import { getEffectiveTenantId, tenantCollection, tenantDoc, tenantPatientCollection } from './tenantContext';

export const MAX_CARE_MANAGERS = 4;

/**
 * Display label for patient PWA Call tab / denormalized `careManagerDisplayName` (primary CM only).
 * Matches trilogy-one patientService.careManagerLabelFromUserDoc preference order.
 */
export function formatCareManagerDisplayNameFromUserDoc(data) {
  if (!data || typeof data !== 'object') return null;
  const displayName = (data.displayName || '').trim();
  if (displayName) return displayName;
  const full = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  const em = (data.email || '').trim();
  return em || null;
}

function stripTenantSuffixesForPatientDoc(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/,?\s*L\.?L\.?C\.?$/i, '')
    .replace(/,?\s*Inc\.?$/i, '')
    .replace(/,?\s*Ltd\.?$/i, '')
    .trim();
}

/**
 * Patients table / pickers: "First (Display name) Last" from a care `users` row.
 */
export function formatCareAppUserListName(u) {
  if (!u || typeof u !== 'object') return '';
  const first = (u.firstName || '').trim();
  const last = (u.lastName || '').trim();
  const disp = (u.displayName || '').trim();
  const parts = [];
  if (first) parts.push(first);
  if (disp) parts.push(`(${disp})`);
  if (last) parts.push(last);
  if (parts.length) return parts.join(' ');
  return (u.email || '').trim();
}

/**
 * Resolve a care user UID to "First (DisplayName) Last" using a lookup map.
 * @param {string|null|undefined} uid
 * @param {Map<string, object>} [usersById]
 */
export function formatCareAppUserNameByUid(uid, usersById = new Map(), fallbackName = '') {
  const id = String(uid || '').trim();
  if (!id) return '—';
  if (id.startsWith('system:')) return 'System';
  const u = usersById.get(id);
  if (u) {
    const name = formatCareAppUserListName(u);
    if (name) return name;
    const email = (u.email || '').trim();
    if (email) return email;
  }
  const stored = String(fallbackName || '').trim();
  if (stored) return stored;
  return id;
}

/**
 * Fetch user profile rows for the given Firebase Auth UIDs (pathway assigners, etc.).
 * @param {string[]} uids
 * @returns {Promise<Array<{ id: string } & Record<string, unknown>>>}
 */
export async function fetchCareUsersByIds(uids) {
  const unique = [...new Set(
    (uids || [])
      .map((x) => String(x || '').trim())
      .filter((x) => x && !x.startsWith('system:')),
  )];
  if (unique.length === 0) return [];
  const results = await Promise.allSettled(unique.map((uid) => getDoc(doc(db, 'users', uid))));
  return results
    .filter((r) => r.status === 'fulfilled' && r.value.exists())
    .map((r) => ({ id: r.value.id, ...r.value.data() }));
}

async function resolveCareManagerDenormFieldsForPrimaryUid(uid) {
  if (!uid || typeof uid !== 'string') return { displayName: null, phone: null, photoUrl: null };
  try {
    const snap = await getDoc(doc(db, 'users', uid.trim()));
    if (!snap.exists()) return { displayName: null, phone: null, photoUrl: null };
    const data = snap.data();
    const displayName = formatCareManagerDisplayNameFromUserDoc(data);
    const phone = (data.patientFacingPhone || '').trim() || null;
    const photoUrl = (data.photoUrl || '').trim() || null;
    return { displayName, phone, photoUrl };
  } catch {
    return { displayName: null, phone: null, photoUrl: null };
  }
}

async function resolveCareManagerDisplayNameForPrimaryUid(uid) {
  const { displayName } = await resolveCareManagerDenormFieldsForPrimaryUid(uid);
  return displayName;
}

/** UI slots (length MAX) padded from patient; empty strings for unused slots. */
export function careManagerSlotsVectorFromPatient(patient) {
  const ids = getPatientRpmCareManagerIds(patient);
  return Array.from({ length: MAX_CARE_MANAGERS }, (_, i) => ids[i] || '');
}

/**
 * Ordered slot values → deduped `careManagerIds` (primary = first non-empty unique slot).
 */
export function careManagerIdsFromOrderedSlots(slots) {
  const out = [];
  const seen = new Set();
  const arr = Array.isArray(slots) ? slots : [];
  for (let i = 0; i < MAX_CARE_MANAGERS; i++) {
    const v = String(arr[i] || '').trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/**
 * If `data` includes care manager assignment fields, return normalized { careManagerIds, careManagerId }.
 * Returns `null` when the payload does not intend to change assignment.
 */
export function careManagerFieldsFromPatientInput(data) {
  if (!data || typeof data !== 'object') return null;
  if (Array.isArray(data.careManagerIds)) {
    const ids = [...new Set(data.careManagerIds.map((x) => String(x || '').trim()).filter(Boolean))].slice(
      0,
      MAX_CARE_MANAGERS,
    );
    return { careManagerIds: ids, careManagerId: ids[0] ?? null, rpmCareManagerIds: ids };
  }
  if ('careManagerId' in data || 'careManager' in data) {
    const raw = data.careManagerId ?? data.careManager;
    if (raw == null || raw === '') {
      return { careManagerIds: [], careManagerId: null, rpmCareManagerIds: [] };
    }
    const s = String(raw).trim();
    if (!s) return { careManagerIds: [], careManagerId: null, rpmCareManagerIds: [] };
    return { careManagerIds: [s], careManagerId: s, rpmCareManagerIds: [s] };
  }
  return null;
}

function mergePatientDocArrays(docArrays) {
  const byId = new Map();
  for (const docs of docArrays) {
    for (const d of docs) {
      if (!byId.has(d.id)) byId.set(d.id, { ...d.data(), id: d.id });
    }
  }
  return [...byId.values()];
}

/**
 * Effective tenant for list queries. Always returns a tenant — superuser without impersonation
 * defaults to their own tenant ('patientmaestro').
 * @returns {{ mode: 'tenant', tenantId: string } | { mode: 'none' }}
 */
export function getEffectiveTenantScope(u) {
  if (!u) return { mode: 'none' };
  if (u.role === 'superuser') {
    return u.impersonatingTenantId
      ? { mode: 'tenant', tenantId: u.impersonatingTenantId }
      : { mode: 'tenant', tenantId: u.tenantId || 'patientmaestro' };
  }
  return u.tenantId ? { mode: 'tenant', tenantId: u.tenantId } : { mode: 'none' };
}

/**
 * tenantId for new/edited tenant-scoped catalog docs (pathways, education) from signed-in care profile.
 * Superuser without impersonation defaults to trilogy for new catalog rows.
 * @param {{ role?: string, tenantId?: string, impersonatingTenantId?: string } | null} profile
 */
export function tenantIdForCareContentWrites(profile) {
  if (!profile) return 'trilogy';
  const scope = getEffectiveTenantScope({
    role: profile.role,
    tenantId: profile.tenantId,
    impersonatingTenantId: profile.impersonatingTenantId,
  });
  if (scope.mode === 'tenant') return scope.tenantId;
  return profile.tenantId || 'trilogy';
}

/**
 * Superuser without an active impersonated tenant must not perform writes that set or enforce tenantId
 * (pathways, education library, billing catalog, user admin via HTTPS functions, etc.).
 * @param {{ role?: string, impersonatingTenantId?: string } | null} profile - care app AuthContext profile
 */
export function superuserMustImpersonateToWriteTenantData(profile) {
  if (!profile || profile.role !== 'superuser') return false;
  const imp =
    typeof profile.impersonatingTenantId === 'string' ? profile.impersonatingTenantId.trim() : '';
  return !imp;
}

/** Care-app signed-in user + normalized role (for Firestore queries aligned with rules). */
export async function getCareAppUserForPatientQueries() {
  const uid = auth?.currentUser?.uid;
  if (!uid) return null;
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  const data = snap.data();
  const role = String(data.role || '').toLowerCase().trim();
  const tenantId = typeof data.tenantId === 'string' && data.tenantId.trim() ? data.tenantId.trim() : null;
  const impersonatingTenantId =
    typeof data.impersonatingTenantId === 'string' && data.impersonatingTenantId.trim()
      ? data.impersonatingTenantId.trim()
      : null;
  const normIds = (v) => (Array.isArray(v) ? [...new Set(v.map((x) => String(x || '').trim()).filter(Boolean))] : []);
  return {
    uid,
    role,
    tenantId,
    impersonatingTenantId,
    // Viewer role scope filters (empty arrays for other roles).
    accessClientIds: normIds(data.accessClientIds),
    accessProviderIds: normIds(data.accessProviderIds),
  };
}

// --- Validation helpers (for forms and CSV) ---

/** Strip to 10 digits only; return null if not 10 digits. */
export function normalizePhone(value) {
  if (value == null || value === '') return null;
  const digits = String(value).replace(/\D/g, '');
  return digits.length === 10 ? digits : null;
}

/**
 * Check if a normalized phone is already claimed by another patient across all tenants.
 * @returns {{ available: boolean, ownerPatientId?: string, ownerTenantId?: string }}
 */
export async function checkPhoneAvailability(normalizedPhone, excludePatientId = null) {
  if (!normalizedPhone) return { available: true };
  const snap = await getDoc(doc(db, 'patientPhoneIndex', normalizedPhone));
  if (!snap.exists()) return { available: true };
  const data = snap.data();
  if (excludePatientId && data.patientId === excludePatientId) return { available: true };
  return { available: false, ownerPatientId: data.patientId, ownerTenantId: data.tenantId };
}

/** Format 10-digit string as (999) 999-9999. */
export function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const d = String(phone).replace(/\D/g, '').slice(0, 10);
  if (d.length !== 10) return phone;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Minimal email check: has @ and at least a@a.a pattern. */
export function isValidEmail(value) {
  if (value == null || value === '') return true;
  const s = String(value).trim();
  return /^[^@]+@[^@]+\.[^@]+$/.test(s) && s.length >= 5;
}

/**
 * Normalize date from CSV or form to YYYY-MM-DD string for storage and duplicate check.
 * Accepts: YYYY-MM-DD, MM/DD/YYYY, M/D/YYYY, etc.
 */
export function normalizeDate(value) {
  return normalizeYmdDate(value);
}

export async function getPatient(patientId, tenantIdOverride) {
  if (!patientId) return null;
  const tid = tenantIdOverride || await getEffectiveTenantId();
  if (!tid) return null;
  const snap = await getDoc(tenantDoc(tid, 'patients', patientId));
  return snap.exists() ? { ...snap.data(), id: snap.id } : null;
}

function patientsRef(tenantId) {
  return tenantCollection(tenantId, 'patients');
}

/** Firestore `in` filter limit-safe chunking. */
function chunkIds(ids, size = 10) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/**
 * Queries for the read-only viewer role: patients whose denormalized clientId
 * is in the user's accessClientIds and/or whose currentProviderId is in
 * accessProviderIds (kept in sync by the onPatientAccessWritten trigger).
 */
function buildViewerQueries(tid, user) {
  const queries = [];
  for (const chunk of chunkIds(user.accessClientIds || [])) {
    queries.push(query(patientsRef(tid), where('clientId', 'in', chunk)));
  }
  for (const chunk of chunkIds(user.accessProviderIds || [])) {
    queries.push(query(patientsRef(tid), where('currentProviderId', 'in', chunk)));
  }
  return queries;
}

/** Superuser without impersonation sees all tenants; with impersonation, same scope as that tenant’s admin (see PLAN_MULTI_TENANCY Phase 3). */
export async function getPatients() {
  const user = await getCareAppUserForPatientQueries();
  if (!user) return [];

  const { uid, role } = user;
  const scope = getEffectiveTenantScope(user);

  const tid = scope.mode === 'tenant' ? scope.tenantId : null;
  if (!tid) return [];

  if (role === 'admin' || role === 'superuser' || role === 'clinical admin') {
    const snapshot = await getDocs(patientsRef(tid));
    return snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
  }

  if (role === 'care manager') {
    const s1 = await getDocs(query(patientsRef(tid), where('careManagerIds', 'array-contains', uid)));
    return s1.docs.map((d) => ({ ...d.data(), id: d.id }));
  }

  if (role === 'supervisor' || role === 'manager') {
    const [s1, s3] = await Promise.all([
      getDocs(query(patientsRef(tid), where('careManagerIds', 'array-contains', uid))),
      getDocs(query(patientsRef(tid), where('supervisorAccessIds', 'array-contains', uid))),
    ]);
    return mergePatientDocArrays([s1.docs, s3.docs]);
  }

  if (role === 'viewer') {
    const queries = buildViewerQueries(tid, user);
    if (queries.length === 0) return [];
    const snaps = await Promise.all(queries.map((q) => getDocs(q)));
    return mergePatientDocArrays(snaps.map((s) => s.docs));
  }

  return [];
}

/**
 * Build the Firestore query for the current care user's patient access scope.
 * Returns null if no query can be built (not signed in, no tenant).
 * For roles with multiple queries (supervisor/manager/viewer), returns an array of queries.
 */
export async function buildPatientsQuery() {
  const user = await getCareAppUserForPatientQueries();
  if (!user) return null;
  const { uid, role } = user;
  const scope = getEffectiveTenantScope(user);
  const tid = scope.mode === 'tenant' ? scope.tenantId : null;
  if (!tid) return null;
  if (role === 'admin' || role === 'superuser' || role === 'clinical admin') {
    return patientsRef(tid);
  }
  if (role === 'care manager') {
    return query(patientsRef(tid), where('careManagerIds', 'array-contains', uid));
  }
  if (role === 'supervisor' || role === 'manager') {
    return [
      query(patientsRef(tid), where('careManagerIds', 'array-contains', uid)),
      query(patientsRef(tid), where('supervisorAccessIds', 'array-contains', uid)),
    ];
  }
  if (role === 'viewer') {
    const queries = buildViewerQueries(tid, user);
    return queries.length > 0 ? queries : null;
  }
  return null;
}

/**
 * Subscribe to real-time patient list updates.
 * @param {(patients: Array<object>) => void} onData
 * @param {(err: Error) => void} [onError]
 * @returns {Promise<() => void>} unsubscribe function
 */
export async function subscribePatients(onData, onError = (err) => console.warn('Patients subscription error', err)) {
  const q = await buildPatientsQuery();
  if (!q) {
    onData([]);
    return () => {};
  }
  if (Array.isArray(q)) {
    const allDocs = new Map();
    const unsubs = q.map((qr) =>
      onSnapshot(qr, (snap) => {
        snap.docs.forEach((d) => allDocs.set(d.id, { ...d.data(), id: d.id }));
        onData([...allDocs.values()]);
      }, onError),
    );
    return () => unsubs.forEach((u) => u());
  }
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
  }, onError);
}

/**
 * Create a new patient with auto-generated ID patient_000001, patient_000002, ...
 * Uses a transaction on counters/patientId. Required: firstName, lastName, dateOfBirth, mrn, gender.
 * Optional: careManagerId, address fields, phone (normalized), email, insuranceId, insurances (array), active, deviceIds, gender.
 * dateOfBirth should be normalized (e.g. YYYY-MM-DD string); phone will be normalized if provided.
 * insurances: [{ insuranceId, memberID?, effectiveDate, terminationDate? }, ...]. When present, first entry's insuranceId is also written as insuranceId for backward compatibility.
 * diagnoses: optional array of diagnosis rows (validated in UI): { id, icdCode, category?, dxEffectiveDate, dxTerminatedDate, ... }.
 * @param {Object} [auditContext] - { uid, email } — when set, writes patientFieldAuditLog after create.
 */
export async function createPatient(data, auditContext = null) {
  const careUser = await getCareAppUserForPatientQueries();
  if (!careUser) {
    throw new Error('Not signed in.');
  }
  const scope = getEffectiveTenantScope(careUser);
  let tenantId = scope.mode === 'tenant' ? scope.tenantId : null;
  if (!tenantId && typeof careUser.tenantId === 'string' && careUser.tenantId.trim()) {
    tenantId = careUser.tenantId.trim();
  }
  if (!tenantId && data.tenantId) {
    tenantId = String(data.tenantId).trim();
  }
  if (!tenantId) {
    throw new Error('Your account has no tenantId; ask an administrator to set users/{uid}.tenantId before adding patients.');
  }

  const genderVal = data.gender != null && String(data.gender).trim() ? String(data.gender).trim() : null;
  if (!genderVal) throw new Error('gender is required');
  const phoneNorm = normalizePhone(data.phone);
  if (!phoneNorm) throw new Error('A valid 10-digit phone number is required.');
  const phoneCheck = await checkPhoneAvailability(phoneNorm);
  if (!phoneCheck.available) {
    throw new Error('This phone number is already in use by another patient.');
  }
  const cmFields = careManagerFieldsFromPatientInput(data);
  const cmDenorm = cmFields?.careManagerId != null
    ? await resolveCareManagerDenormFieldsForPrimaryUid(cmFields.careManagerId)
    : { displayName: null, phone: null, photoUrl: null };
  let tenantDisplayName = null;
  try {
    const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
    if (tenantSnap.exists()) {
      const td = tenantSnap.data();
      const raw = ((td.shortName || '').trim() || (td.legalName || '').trim());
      tenantDisplayName = stripTenantSuffixesForPatientDoc(raw) || null;
    }
  } catch { /* ignore */ }
  /**
   * Auto-suspend care alerts for new patients for a tenant-configurable window (default 3 days)
   * so a first device sync of stored readings does not generate a flood of alerts. Read the
   * setting inline (rather than importing careAlertSettingsService) to avoid a circular import,
   * since tenantContext already imports from this module.
   */
  let newPatientSuspendDays = 3;
  try {
    const sSnap = await getDoc(tenantDoc(tenantId, 'settings', 'careAlerts'));
    if (sSnap.exists()) {
      const d = Number(sSnap.data().newPatientSuspendDays);
      if (Number.isFinite(d) && d >= 0) newPatientSuspendDays = Math.min(Math.round(d), 365);
    }
  } catch { /* default */ }
  const alertsSuspendFields = newPatientSuspendDays > 0
    ? {
        alertsSuspended: true,
        alertsSuspendedAt: serverTimestamp(),
        alertsSuspendedBy: 'system',
        alertsSuspendedByName: 'Auto (new patient)',
        alertsSuspendedUntil: new Date(Date.now() + newPatientSuspendDays * 86400000),
      }
    : null;
  const counterRef = tenantDoc(tenantId, 'counters', 'patientId');
  let newId;
  await runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(counterRef);
    const lastNumber = counterSnap.exists() ? (counterSnap.data().lastNumber ?? 0) : 0;
    const next = lastNumber + 1;
    newId = `patient_${String(next).padStart(6, '0')}`;
    tx.set(counterRef, { lastNumber: next }, { merge: true });
    const hasInsurances = Array.isArray(data.insurances) && data.insurances.length > 0;
    const firstInsuranceId = hasInsurances && data.insurances[0].insuranceId ? data.insurances[0].insuranceId : null;
    const payload = {
      tenantId,
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth,
      mrn: data.mrn,
      gender: genderVal,
      active: data.active !== false,
      ...(data.preferredName != null && data.preferredName !== '' && { preferredName: data.preferredName }),
      ...(data.middleName != null && String(data.middleName).trim() !== '' && { middleName: String(data.middleName).trim() }),
      ...(data.suffix != null && String(data.suffix).trim() !== '' && { suffix: String(data.suffix).trim() }),
      ...(data.excludeFromSurveys === true && { excludeFromSurveys: true }),
      ...(cmFields && {
        careManagerIds: cmFields.careManagerIds,
        careManagerId: cmFields.careManagerId,
        rpmCareManagerIds: cmFields.rpmCareManagerIds,
        careManagerDisplayName: cmFields.careManagerId ? cmDenorm.displayName : null,
        careManagerPhone: cmFields.careManagerId ? cmDenorm.phone : null,
        careManagerPhotoUrl: cmFields.careManagerId ? cmDenorm.photoUrl : null,
      }),
      ...(tenantDisplayName && { tenantDisplayName }),
      ...(data.addressStreet1 != null && data.addressStreet1 !== '' && { addressStreet1: data.addressStreet1 }),
      ...(data.addressStreet2 != null && data.addressStreet2 !== '' && { addressStreet2: data.addressStreet2 }),
      ...(data.addressCity != null && data.addressCity !== '' && { addressCity: data.addressCity }),
      ...(data.addressState != null && data.addressState !== '' && { addressState: data.addressState }),
      ...(data.addressZip != null && data.addressZip !== '' && { addressZip: data.addressZip }),
      phone: phoneNorm,
      ...(data.email != null && data.email !== '' && { email: data.email }),
      ...(hasInsurances && { insurances: data.insurances }),
      ...(firstInsuranceId && { insuranceId: firstInsuranceId }),
      ...(!hasInsurances && data.insuranceId != null && data.insuranceId !== '' && { insuranceId: data.insuranceId }),
      ...(Array.isArray(data.deviceIds) && data.deviceIds.length > 0 && { deviceIds: data.deviceIds }),
      ...(data.clientSiteId != null && data.clientSiteId !== '' && { clientSiteId: data.clientSiteId }),
      ...(Array.isArray(data.providerAssignments) && data.providerAssignments.length > 0 && { providerAssignments: data.providerAssignments }),
      ...(Array.isArray(data.diagnoses) && data.diagnoses.length > 0 && { diagnoses: data.diagnoses }),
      ...(data.otherPhone != null && data.otherPhone !== '' && { otherPhone: data.otherPhone }),
      ...(Array.isArray(data.emergencyContacts) && data.emergencyContacts.length > 0 && { emergencyContacts: data.emergencyContacts }),
      ...(data.outreachUserUid && {
        outreachUserUid: data.outreachUserUid,
        outreachUserName: data.outreachUserName || null,
        outreachUserUpdatedAt: serverTimestamp(),
        outreachUserUpdatedByUid: data.outreachUserUpdatedByUid || null,
      }),
      ...(alertsSuspendFields || {}),
      systemEntryDate: serverTimestamp(),
    };
    tx.set(tenantDoc(tenantId, 'patients', newId), payload);
  });
  if (auditContext?.uid) {
    try {
      const pr = await getPatient(newId);
      await logPatientAuditCreate({
        patientId: newId,
        actorUid: auditContext.uid,
        actorEmail: auditContext.email || null,
        snapshot: pr,
      });
    } catch (e) {
      console.warn('patientFieldAuditLog create log failed', e);
    }
  }
  return newId;
}

/**
 * Find potential duplicates: same firstName, lastName, and dateOfBirth (excluding optional excludePatientId).
 * dateOfBirth must be normalized string (YYYY-MM-DD).
 */
export async function findPotentialDuplicates(firstName, lastName, dateOfBirth, excludePatientId = null) {
  if (!firstName || !lastName || !dateOfBirth) return [];
  try {
    const careUser = await getCareAppUserForPatientQueries();
    const tid = careUser?.role === 'superuser' && careUser.impersonatingTenantId
      ? careUser.impersonatingTenantId
      : careUser?.tenantId || null;
    if (!tid) return [];
    const constraints = [
      where('firstName', '==', firstName),
      where('lastName', '==', lastName),
      where('dateOfBirth', '==', dateOfBirth),
    ];
    const q = query(tenantCollection(tid, 'patients'), ...constraints);
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
    if (excludePatientId) return list.filter((p) => p.id !== excludePatientId);
    return list;
  } catch (e) {
    console.warn('findPotentialDuplicates failed', e);
    return [];
  }
}

/**
 * Update a patient document (partial update). Used e.g. for active flag, careManagerId.
 * @param {string} patientId
 * @param {Object} data - fields to update (e.g. { active: true }, { careManagerId: uid })
 * @param {Object} [auditContext] - { user: { uid, email }, previousPatient } — when set, appends patientFieldAuditLog for changed keys.
 */
export async function updatePatient(patientId, data, auditContext = null) {
  if (!patientId || !data || typeof data !== 'object') return;
  if ('phone' in data) {
    if (data.phone == null || data.phone === '' || String(data.phone).trim() === '') {
      throw new Error('Phone number cannot be empty.');
    }
    const phoneNorm = normalizePhone(data.phone);
    if (!phoneNorm) throw new Error('Phone must be a valid 10-digit number.');
    const phoneCheck = await checkPhoneAvailability(phoneNorm, patientId);
    if (!phoneCheck.available) {
      throw new Error('This phone number is already in use by another patient.');
    }
    data = { ...data, phone: phoneNorm };
  }
  const patch = { ...data };
  const cmFields = careManagerFieldsFromPatientInput(data);
  if (cmFields) {
    Object.assign(patch, cmFields);
    delete patch.careManager;
    const cmDenorm = cmFields.careManagerId
      ? await resolveCareManagerDenormFieldsForPrimaryUid(cmFields.careManagerId)
      : { displayName: null, phone: null, photoUrl: null };
    patch.careManagerDisplayName = cmDenorm.displayName ?? null;
    patch.careManagerPhone = cmDenorm.phone ?? null;
    patch.careManagerPhotoUrl = cmDenorm.photoUrl ?? null;
  }
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant for patient update');
  for (const key of Object.keys(patch)) {
    if (patch[key] === undefined) delete patch[key];
  }
  await updateDoc(tenantDoc(tenantId, 'patients', patientId), patch);
  if ('active' in patch || 'patientLoginEnabled' in patch) {
    try {
      const fn = httpsCallable(functions, 'syncPatientLoginAccess');
      await fn({ patientId });
    } catch (e) {
      console.warn('syncPatientLoginAccess call failed', e?.message || e);
    }
  }
  if (auditContext?.previousPatient != null && auditContext?.user?.uid) {
    const changes = computePatientFieldChanges(auditContext.previousPatient, patch);
    if (Object.keys(changes).length > 0) {
      try {
        await logPatientAuditUpdate({
          patientId,
          actorUid: auditContext.user.uid,
          actorEmail: auditContext.user.email || null,
          changes,
        });
      } catch (e) {
        console.warn('patientFieldAuditLog write failed', e);
      }
    }
  }
}

/** Readings fetched per page on the patient detail Readings tab. */
export const PATIENT_READINGS_PAGE_SIZE = 1000;

/**
 * One page of a patient's readings, newest received first.
 * Pass the previous page's `lastDoc` as `startAfterDoc` to page further back.
 * @param {string} patientId
 * @param {{ pageSize?: number, startAfterDoc?: import('firebase/firestore').QueryDocumentSnapshot|null }} [opts]
 * @returns {Promise<{ readings: object[], lastDoc: import('firebase/firestore').QueryDocumentSnapshot|null, hasMore: boolean }>}
 */
export async function getPatientReadings(patientId, opts = {}) {
  const empty = { readings: [], lastDoc: null, hasMore: false };
  if (!patientId) return empty;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return empty;
  const pageSize = opts.pageSize ?? PATIENT_READINGS_PAGE_SIZE;
  const col = tenantPatientCollection(tenantId, patientId, 'readings');
  const base = [orderBy('storedAt', 'desc')];
  const q = opts.startAfterDoc
    ? query(col, ...base, startAfter(opts.startAfterDoc), limit(pageSize))
    : query(col, ...base, limit(pageSize));
  const snapshot = await getDocs(q);
  return {
    readings: snapshot.docs.map((d) => ({ ...d.data(), id: d.id, patientId })),
    lastDoc: snapshot.docs[snapshot.docs.length - 1] ?? null,
    hasMore: snapshot.docs.length === pageSize,
  };
}

/** Upper bound on voided readings loaded for one patient (voids are rare). */
const VOIDED_READINGS_LIMIT = 500;

/**
 * A patient's voided readings (they have no `storedAt`, so `getPatientReadings`
 * never returns them). `voidedStoredAt` is mapped onto `storedAt` for display so
 * the Readings tab's period filter, sort, and date helpers work unchanged.
 * Only call for roles with `canVoidReadings`.
 * @param {string} patientId
 * @returns {Promise<object[]>}
 */
export async function getVoidedPatientReadings(patientId) {
  if (!patientId) return [];
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return [];
  const col = tenantPatientCollection(tenantId, patientId, 'readings');
  const snapshot = await getDocs(query(col, where('voided', '==', true), limit(VOIDED_READINGS_LIMIT)));
  return snapshot.docs.map((d) => {
    const data = d.data();
    return { ...data, storedAt: data.voidedStoredAt ?? null, id: d.id, patientId };
  });
}

export function getConversationId(patientId, careManagerId) {
  const a = patientId || '';
  const b = careManagerId || '';
  return a < b ? `${a}_${b}` : `${b}_${a}`;
}

export { getPatientCareTeamThreadId } from '../utils/messagingThreadIds';

export function subscribeToMessages(conversationId, callback) {
  if (!conversationId) return () => {};
  let cancelled = false;
  let unsubscribe = () => {};
  (async () => {
    const user = await getCareAppUserForPatientQueries();
    const scope = getEffectiveTenantScope(user);
    const tid = scope.mode === 'tenant' ? scope.tenantId : null;
    if (!tid) { callback([]); return; }
    const q = query(tenantCollection(tid, 'messages'), where('conversationId', '==', conversationId));
    if (cancelled) return;
    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt,
        }));
        list.sort((a, b) => {
          const toMs = (v) => (v && typeof v.getTime === 'function' ? v.getTime() : v ? new Date(v).getTime() : 0);
          return toMs(a.createdAt) - toMs(b.createdAt);
        });
        callback(list);
      },
      (err) => {
        console.warn('Messages subscription error:', err);
        callback([]);
      },
    );
  })();
  return () => {
    cancelled = true;
    unsubscribe();
  };
}

export async function sendMessage(payload) {
  const { conversationId, senderId, recipientId, body, senderRole, senderDisplayName, patientId } = payload;
  let tenantId = 'trilogy';
  if (patientId) {
    try {
      const p = await getPatient(patientId);
      const t = p?.tenantId;
      if (typeof t === 'string' && t.trim()) tenantId = t.trim();
    } catch {
      /* keep default */
    }
  }
  const messageRef = await addDoc(tenantCollection(tenantId, 'messages'), {
    conversationId,
    patientId: patientId || null,
    tenantId,
    senderId,
    senderRole: senderRole || 'care manager',
    ...(senderDisplayName ? { senderDisplayName } : {}),
    recipientId,
    body,
    createdAt: serverTimestamp(),
    readAt: null,
  });
  if ((senderRole || 'care manager') !== 'patient') {
    recordDailyWin({
      uid: senderId,
      type: 'messageSent',
      patientId,
      eventId: `message:${messageRef.id}`,
    });
  }
}

/**
 * Marks outbound care-team messages to this patient as read by the patient (`readAt`).
 * Scoped by patientId so legacy per-CM conversation threads are included.
 */
export async function markOutboundCareMessagesReadForPatient(patientId) {
  if (!patientId) return;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return;
  const q = query(
    tenantCollection(tenantId, 'messages'),
    where('patientId', '==', patientId),
    where('readAt', '==', null),
  );
  const snapshot = await getDocs(q);
  const toMark = snapshot.docs.filter((d) => d.data().senderRole === 'care manager');
  const chunkSize = 500;
  for (let i = 0; i < toMark.length; i += chunkSize) {
    const slice = toMark.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    slice.forEach((docSnap) => {
      batch.update(tenantDoc(tenantId, 'messages', docSnap.id), { readAt: serverTimestamp() });
    });
    await batch.commit();
  }
}

export async function voidMessage(tenantId, messageId, userId) {
  if (!tenantId || !messageId || !userId) return;
  await updateDoc(tenantDoc(tenantId, 'messages', messageId), {
    voidedAt: serverTimestamp(),
    voidedBy: userId,
  });
}

/** Patient-originated messages for this patient (dashboard unread list uses `readAt`). */
export async function markPatientMessagesReadForPatient(patientId) {
  if (!patientId) return;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return;
  const q = query(
    tenantCollection(tenantId, 'messages'),
    where('patientId', '==', patientId),
    where('readAt', '==', null),
  );
  const snapshot = await getDocs(q);
  const toMark = snapshot.docs.filter((d) => {
    const data = d.data();
    return data.senderRole === 'patient' || data.senderType === 'patient';
  });
  const chunkSize = 500;
  for (let i = 0; i < toMark.length; i += chunkSize) {
    const slice = toMark.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    slice.forEach((docSnap) => {
      batch.update(tenantDoc(tenantId, 'messages', docSnap.id), { readAt: serverTimestamp() });
    });
    await batch.commit();
  }
}
